// Svelte use: action for drawer drag-to-resize
// Ported from public/main.js initDrawerDrag (lines 294-506)

const COLLAPSED_HEIGHT = 55;
const EXPANDED_THRESHOLD = 150;
const VELOCITY_THRESHOLD = 0.5; // px/ms — flick detection

/**
 * Svelte action for drag-to-resize on a drawer header element.
 *
 * @param {HTMLElement} headerNode - The header element to attach drag events to
 * @param {Object} options
 * @param {HTMLElement} options.container - The drawer container element
 * @param {HTMLElement} [options.overlay] - Optional overlay element to fade
 * @param {(expanded: boolean) => void} [options.onStateChange] - Called when snap resolves
 * @param {() => void} [options.onTap] - Called on tap (no drag) — mobile and desktop
 * @param {(progress: number) => void} [options.onDragProgress] - Called with 0-1 progress during drag
 */
export function dragresize(headerNode, options) {
  let { container, overlay, onStateChange, onTap, onDragProgress } = options;

  let isDragging = false;
  let touchOffsetFromTop = 0;
  let lastY = 0;
  let lastTime = 0;
  let velocity = 0;
  let touchStartY = 0;
  let touchMoved = false;

  function getMaxHeight() {
    const vh = window.innerHeight;
    return window.innerWidth <= 768 ? vh * 0.75 : vh * 0.5;
  }

  function handleDragStart(clientY) {
    if (!container) return false;

    // Clear any active transition styles to prevent fighting if caught mid-animation
    container.style.transitionDuration = '';
    container.style.transitionTimingFunction = '';

    isDragging = true;
    const drawerTop = container.getBoundingClientRect().top;
    touchOffsetFromTop = clientY - drawerTop;
    lastY = clientY;
    lastTime = Date.now();
    velocity = 0;

    container.classList.add('dragging');

    // Show overlay immediately so we can fade it during drag
    if (overlay) {
      overlay.classList.add('is-visible');
      overlay.style.opacity = String(
        (container.offsetHeight - COLLAPSED_HEIGHT) / (getMaxHeight() - COLLAPSED_HEIGHT)
      );
    }

    return true;
  }

  function handleDragMove(clientY) {
    if (!isDragging) return;

    const now = Date.now();
    const deltaTime = now - lastTime;
    if (deltaTime > 0) {
      velocity = (lastY - clientY) / deltaTime;
    }
    lastY = clientY;
    lastTime = now;

    const vh = window.innerHeight;
    const maxHeight = getMaxHeight();
    const minFingerY = vh - maxHeight + touchOffsetFromTop;
    const maxFingerY = vh - COLLAPSED_HEIGHT + touchOffsetFromTop;
    const clampedClientY = Math.min(Math.max(minFingerY, clientY), maxFingerY);

    const newDrawerTop = clampedClientY - touchOffsetFromTop;
    const newHeight = vh - newDrawerTop;

    container.style.height = `${newHeight}px`;

    // Fade overlay based on drawer height
    const progress = (newHeight - COLLAPSED_HEIGHT) / (maxHeight - COLLAPSED_HEIGHT);
    if (overlay) overlay.style.opacity = String(progress);
    if (onDragProgress) onDragProgress(progress);
  }

  function handleDragEnd() {
    if (!isDragging) return;

    isDragging = false;
    const currentHeight = container.offsetHeight;

    // Determine snap position based on velocity or position
    let shouldExpand;
    if (Math.abs(velocity) > VELOCITY_THRESHOLD) {
      shouldExpand = velocity > 0;
    } else {
      shouldExpand = currentHeight > EXPANDED_THRESHOLD;
    }

    // Calculate momentum-based duration
    const targetH = shouldExpand ? getMaxHeight() : COLLAPSED_HEIGHT;
    const distance = Math.abs(targetH - currentHeight);

    let duration = 300;
    if (Math.abs(velocity) > 0.05) {
      duration = distance / Math.abs(velocity);
      duration = Math.min(Math.max(duration, 200), 500);
    }

    // Apply dynamic physics styles
    container.style.transitionDuration = `${duration}ms`;
    container.style.transitionTimingFunction = 'ease-out';

    container.classList.remove('dragging');

    // Clear inline opacity so CSS transitions take over
    if (overlay) overlay.style.opacity = '';

    if (shouldExpand) {
      container.classList.add('expanded');
      if (overlay) overlay.classList.add('is-visible');
    } else {
      container.classList.remove('expanded');
      if (overlay) overlay.classList.remove('is-visible');
    }

    // Clear inline height so CSS class handles the snap smoothly
    container.style.height = '';

    // Cleanup inline styles after animation finishes
    setTimeout(() => {
      container.style.transitionDuration = '';
      container.style.transitionTimingFunction = '';
    }, duration);

    if (onStateChange) onStateChange(shouldExpand);
  }

  // --- Touch events (mobile): supports both drag and tap-to-toggle ---
  function onTouchStart(e) {
    touchStartY = e.touches[0].clientY;
    touchMoved = false;
    if (handleDragStart(e.touches[0].clientY)) {
      e.preventDefault();
    }
  }

  function onTouchMove(e) {
    if (isDragging) {
      if (Math.abs(e.touches[0].clientY - touchStartY) > 10) {
        touchMoved = true;
      }
      e.preventDefault();
      handleDragMove(e.touches[0].clientY);
    }
  }

  function onTouchEnd() {
    handleDragEnd();
    if (!touchMoved && onTap) {
      onTap();
    }
  }

  // --- Desktop: click to toggle (no dragging) ---
  function onClick() {
    if (onTap) onTap();
  }

  headerNode.addEventListener('touchstart', onTouchStart, { passive: false });
  headerNode.addEventListener('touchmove', onTouchMove, { passive: false });
  headerNode.addEventListener('touchend', onTouchEnd);
  headerNode.addEventListener('touchcancel', handleDragEnd);
  headerNode.addEventListener('click', onClick);

  return {
    update(newOptions) {
      container = newOptions.container;
      overlay = newOptions.overlay;
      onStateChange = newOptions.onStateChange;
      onTap = newOptions.onTap;
      onDragProgress = newOptions.onDragProgress;
    },
    destroy() {
      headerNode.removeEventListener('touchstart', onTouchStart);
      headerNode.removeEventListener('touchmove', onTouchMove);
      headerNode.removeEventListener('touchend', onTouchEnd);
      headerNode.removeEventListener('touchcancel', handleDragEnd);
      headerNode.removeEventListener('click', onClick);
    },
  };
}
